import {
  type FilesHttpGetAssetsByAssetIdFilesByFileIdMultipartUrlPartQuery,
  type FilesHttpGetAssetsByAssetIdFilesByFileIdMultipartUrlQuery,
  type FilesMultiPartS3UrlPartsSchema,
  type FilesMultipartB2CancelUpload,
  type FilesMultipartB2FinishUpload,
  type FilesMultipartB2StartUpload,
  type FilesMultipartUploadCleanupSchema,
  type FilesMultipartUploadComposeSchema,
  httpDeleteAssetsByAssetIdFilesByFileId,
  httpDeleteAssetsByAssetIdProxiesByProxyId,
  httpGetAssetsByAssetIdFilesByFileIdMultipartUrl,
  httpGetAssetsByAssetIdFilesByFileIdMultipartUrlPart,
  httpGetAssetsByAssetIdProxiesByProxyIdMultipartUrl,
  httpGetAssetsByAssetIdProxiesByProxyIdMultipartUrlPart,
  httpPostAssetsByAssetIdFilesByFileIdMultipartB2Cancel,
  httpPostAssetsByAssetIdFilesByFileIdMultipartB2Finish,
  httpPostAssetsByAssetIdFilesByFileIdMultipartB2Start,
  httpPostAssetsByAssetIdFilesByFileIdMultipartCleanup,
  httpPostAssetsByAssetIdFilesByFileIdMultipartGcsComposeUrl,
  httpPostAssetsByAssetIdFilesByFileIdMultipartUrlS3Part,
  httpPostAssetsByAssetIdProxiesByProxyIdMultipartCleanup,
} from '@iconik-io/iconik-api/src/services/files';

/**
 * The multipart API is exposed once per uploadable entity — files and proxies
 * today, other upload kinds later. Uploaders address that entity through this
 * port instead of hardcoding the file endpoints, so the byte-transfer code
 * stays agnostic of which entity it is writing to.
 *
 * Not every entity supports every operation. Unsupported operations are absent
 * rather than throwing stubs, so an uploader can gate on the capability with a
 * plain undefined check in validate().
 *
 * `entityId` is the id of the entity being uploaded to, which is the file id
 * for the file family and the proxy id for the proxy one.
 *
 * Each operation returns a union of the families, because the endpoint builders
 * differ in their `id` literal and in the shape of `params`. Both families
 * answer with the same response bodies, so the union collapses at the call site
 * and uploader code never has to distinguish them.
 */
export type MultipartEndpoints = {
  multipartUrl: (
    assetId: string,
    entityId: string,
    query?: FilesHttpGetAssetsByAssetIdFilesByFileIdMultipartUrlQuery,
  ) =>
    | ReturnType<typeof httpGetAssetsByAssetIdFilesByFileIdMultipartUrl>
    | ReturnType<typeof httpGetAssetsByAssetIdProxiesByProxyIdMultipartUrl>;

  multipartUrlPart: (
    assetId: string,
    entityId: string,
    query?: FilesHttpGetAssetsByAssetIdFilesByFileIdMultipartUrlPartQuery,
  ) =>
    | ReturnType<typeof httpGetAssetsByAssetIdFilesByFileIdMultipartUrlPart>
    | ReturnType<typeof httpGetAssetsByAssetIdProxiesByProxyIdMultipartUrlPart>;

  multipartCleanup: (
    assetId: string,
    entityId: string,
    body: FilesMultipartUploadCleanupSchema,
  ) =>
    | ReturnType<typeof httpPostAssetsByAssetIdFilesByFileIdMultipartCleanup>
    | ReturnType<
        typeof httpPostAssetsByAssetIdProxiesByProxyIdMultipartCleanup
      >;

  deleteEntity: (
    assetId: string,
    entityId: string,
  ) =>
    | ReturnType<typeof httpDeleteAssetsByAssetIdFilesByFileId>
    | ReturnType<typeof httpDeleteAssetsByAssetIdProxiesByProxyId>;

  /** Per-part presigned URLs carrying an MD5 checksum. S3 only. */
  s3ChecksummedUrlPart?: (
    assetId: string,
    entityId: string,
    body: FilesMultiPartS3UrlPartsSchema,
  ) => ReturnType<
    typeof httpPostAssetsByAssetIdFilesByFileIdMultipartUrlS3Part
  >;

  /** Composing uploaded parts into the final object. GCS only. */
  gcsComposeUrl?: (
    assetId: string,
    entityId: string,
    body: FilesMultipartUploadComposeSchema,
  ) => ReturnType<
    typeof httpPostAssetsByAssetIdFilesByFileIdMultipartGcsComposeUrl
  >;

  /** B2 drives its multipart upload through its own native API. */
  b2?: {
    start: (
      assetId: string,
      entityId: string,
      body: FilesMultipartB2StartUpload,
    ) => ReturnType<
      typeof httpPostAssetsByAssetIdFilesByFileIdMultipartB2Start
    >;
    finish: (
      assetId: string,
      entityId: string,
      body: FilesMultipartB2FinishUpload,
    ) => ReturnType<
      typeof httpPostAssetsByAssetIdFilesByFileIdMultipartB2Finish
    >;
    cancel: (
      assetId: string,
      entityId: string,
      body: FilesMultipartB2CancelUpload,
    ) => ReturnType<
      typeof httpPostAssetsByAssetIdFilesByFileIdMultipartB2Cancel
    >;
  };
};

/** Uploading to a file entity, which supports the whole multipart API. */
export const fileMultipartEndpoints: MultipartEndpoints = {
  multipartUrl: httpGetAssetsByAssetIdFilesByFileIdMultipartUrl,
  multipartUrlPart: httpGetAssetsByAssetIdFilesByFileIdMultipartUrlPart,
  multipartCleanup: httpPostAssetsByAssetIdFilesByFileIdMultipartCleanup,
  deleteEntity: httpDeleteAssetsByAssetIdFilesByFileId,
  s3ChecksummedUrlPart: httpPostAssetsByAssetIdFilesByFileIdMultipartUrlS3Part,
  gcsComposeUrl: httpPostAssetsByAssetIdFilesByFileIdMultipartGcsComposeUrl,
  b2: {
    start: httpPostAssetsByAssetIdFilesByFileIdMultipartB2Start,
    finish: httpPostAssetsByAssetIdFilesByFileIdMultipartB2Finish,
    cancel: httpPostAssetsByAssetIdFilesByFileIdMultipartB2Cancel,
  },
};

/**
 * Uploading to a proxy entity. The API offers only the three shared multipart
 * operations here: there are no checksummed S3 parts, no GCS compose and no
 * native B2 calls, and the proxy cleanup body cannot even describe a compose,
 * so GCS proxies go up as a whole file instead.
 */
export const proxyMultipartEndpoints: MultipartEndpoints = {
  multipartUrl: httpGetAssetsByAssetIdProxiesByProxyIdMultipartUrl,
  multipartUrlPart: httpGetAssetsByAssetIdProxiesByProxyIdMultipartUrlPart,
  multipartCleanup: httpPostAssetsByAssetIdProxiesByProxyIdMultipartCleanup,
  deleteEntity: httpDeleteAssetsByAssetIdProxiesByProxyId,
};
